import { z } from 'zod';
import { ClientAuditorDto } from './client-auditor.dto';
import { PhoneNumberDto } from './interactions';
import { InternalPhoneNumberDto } from './internal-phone-number.dto';
import { UserTeamDto } from './user-team.dto';
import { TIMEZONE_OPTIONS } from '../../../constants/';
import { assignableRoles, UserRole } from '../../../definitions/';

export interface UserAdministrationDto {
  id: string;
  firstName: string;
  lastName: string;
  avonId: string;
  email: string;
  role: UserRole;
  mobilePhone: PhoneNumberDto;
  resourcingNumber: InternalPhoneNumberDto;
  timeZone: string;
  voicemailFileId: string;
  teams: UserTeamDto[];
  managedClients: ClientAuditorDto[];
  hasAppAccess: boolean;
}

export interface UserTeamCreateDto {
  teamId?: number;
  isPrimary?: boolean;
  isManager?: boolean;
  startDate?: number;
  endDate?: number;
}

export interface AvonSpecializationDto {
  id: string;
  type: string;
  name: string;
  account: string;
}

export interface AvonMedicalCenterDto {
  id: string;
  name: string;
}

export interface CreateAdministrationUserDto {
  firstName: string;
  lastName: string;
  email: string;
  mobilePhone: string;
  resourceNumber: string;
  role: UserRole;
  timeZone: string;
  teams: UserTeamCreateDto[];
  clientAuditors: {
    clientId: number;
    startDate?: number;
    endDate?: number;
  }[];
  specialization?: string;
  medical_centers?: string[];
  createAvonUser?: boolean;
  hasAppAccess?: boolean;
}

export interface UpdateAdministrationUserDto {
  firstName?: string;
  lastName?: string;
  mobilePhone?: string;
  resourceNumber?: string;
  role?: UserRole;
  timeZone?: string;
  teams?: UserTeamCreateDto[];
  clientAuditors?: {
    clientId: number;
    startDate?: number;
    endDate?: number;
  }[];
  avonId?: string;
  medical_centers?: string[];
  specialization?: string;
  createAvonUser?: boolean;
  hasAppAccess?: boolean;
}

const sharedSchemaRules = z.object({
  clientAuditors: z
    .array(
      z.object({
        clientId: z.number().int(),
        startDate: z.number().int().optional().nullable(),
        endDate: z.number().int().optional().nullable(),
      })
    )
    .optional(),
  teams: z
    .array(
      z.object({
        teamId: z.number().int(),
        isPrimary: z.boolean(),
        isManager: z.boolean(),
        startDate: z.number().int().optional().nullable(),
        endDate: z.number().int().optional().nullable(),
      })
    )
    .optional()
    // Only one team can be primary if there are any teams.
    .refine(
      data => {
        if (!data || data.length === 0) {
          return true;
        }
        return data.filter(team => team.isPrimary).length <= 1;
      },
      {
        message: 'Only one team can be primary',
      }
    ),
  role: z
    .nativeEnum(UserRole)
    .optional()
    .refine(
      data => {
        if (!data) {
          return true;
        }
        // Only certain roles are allowed to be created
        return assignableRoles.includes(data);
      },
      data => {
        return {
          message: `Assigning a user with the role of ${data} is not allowed`,
        };
      }
    ),
  hasAppAccess: z.boolean().optional().default(false),
});

export const UpdateUserRequestFormSchema = sharedSchemaRules.extend({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  mobilePhone: z
    .string()
    .regex(/^\+1\d{10}$/, 'Phone number is required and must be 10 digits')
    .optional(),
  timeZone: z.enum(TIMEZONE_OPTIONS as [string, ...string[]], {
    message: 'Time zone is required',
  }),
  resourcingNumberBase: z.string().optional().nullable(),
  specialization: z.string().optional().nullable(),
  medical_centers: z.array(z.string()).optional().nullable(),
  createAvonUser: z.boolean().optional().default(false),
});

export const CreateUserRequestFormSchema = sharedSchemaRules
  .extend({
    firstName: z.string().min(1, { message: 'First name is required' }),
    lastName: z.string().min(1, { message: 'Last name is required' }),
    mobilePhone: z
      .string()
      .regex(/^\+1\d{10}$/, 'Phone number is required and must be 10 digits'),
    email: z.string().email({ message: 'Email is required' }),
    timeZone: z.enum(TIMEZONE_OPTIONS as [string, ...string[]], {
      message: 'Time zone is required',
    }),
    resourceNumber: z
      .string()
      .regex(/^\+1\d{10}$/)
      .optional()
      .nullable(),
  })
  .superRefine((data, ctx) => {
    // If the user is a guide or guide supervisor, they need a resourcing number
    if (
      data.role === UserRole.GUIDE ||
      data.role === UserRole.GUIDE_SUPERVISOR
    ) {
      if (!data.resourceNumber) {
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_type,
          expected: 'string',
          received: typeof data.resourceNumber,
          path: ['resourceNumber'],
          message:
            'Resourcing number is required for guides and guide supervisors',
        });
      }
    }
    if (data.email) {
      const splitEmail = data.email.split('@');
      if (splitEmail.length > 1 && splitEmail[1] !== 'reemahealth.com') {
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_type,
          expected: 'string',
          received: typeof data.email,
          path: ['email'],
          message: 'Must be a Reema Health email address',
        });
      }
    }
  });
export const baseNumberSchema = z
  .object({
    baseNumber: z.coerce.number().min(100).max(999),
  })
  .strict();

// Data type creating user
type CreateUserRequestForm = z.infer<typeof CreateUserRequestFormSchema>;
export type CreateUserSafeParseResponse = z.SafeParseReturnType<
  CreateUserRequestForm,
  CreateUserRequestForm
>;

// Data type updating user
type UpdateUserRequestForm = z.infer<typeof UpdateUserRequestFormSchema>;
export type UpdateUserRequestFormParseResponse = z.SafeParseReturnType<
  UpdateUserRequestForm,
  UpdateUserRequestForm
>;
